import GitHubIcon from '@mui/icons-material/GitHub';
import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import { useI18n } from '../../contexts/I18nContext';
import { useUserStore } from '../../stores/userStore';

interface LoginProps {
  onSwitchToRegister: () => void;
}

const Login: React.FC<LoginProps> = ({ onSwitchToRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, isLoading, error, clearError } = useUserStore();
  const { t } = useI18n();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await login(email, password);
  };

  const handleGithubLogin = () => {
    const envOrigin = (import.meta as any).env?.VITE_AUTH_ORIGIN as string | undefined;
    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const fallbackProd = 'https://www.conformitycore.com';
    const authOrigin = envOrigin || (isLocalhost ? fallbackProd : window.location.origin);
    const startUrl = `${authOrigin}/auth/github/start`;
    window.location.href = startUrl;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t('auth.login.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('auth.login.subtitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label={t('auth.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{ mb: 2 }}
            disabled={isLoading}
          />

          <TextField
            fullWidth
            label={t('auth.password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            sx={{ mb: 3 }}
            disabled={isLoading}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || !email || !password}
            sx={{ mb: 2 }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : t('auth.login.button')}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
            OR
          </Typography>

          <Button
            onClick={handleGithubLogin}
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<GitHubIcon />}
            sx={{ mb: 2 }}
          >
            {t('auth.login.continueWithGithub') || 'Continue with GitHub'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<GoogleIcon />}
            sx={{ mb: 2 }}
            disabled
          >
            Continue with Google
          </Button>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<MicrosoftIcon />}
            sx={{ mb: 2 }}
            disabled
          >
            Continue with Microsoft
          </Button>

          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('auth.login.noAccount')}{' '}
            <Link
              component="button"
              variant="body2"
              onClick={onSwitchToRegister}
              disabled={isLoading}
            >
              {t('auth.login.registerLink')}
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
